import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";

const AlertContext = createContext(null);
const ENTER_ANIMATION_MS = 24;
const EXIT_ANIMATION_MS = 220;

const POSITION_CLASSES = {
  "top-right": "fixed right-7 top-7 z-[120] flex w-[min(92vw,380px)] flex-col gap-2.5",
  "bottom-center": "fixed bottom-7 left-1/2 z-[120] flex w-[min(92vw,380px)] -translate-x-1/2 flex-col gap-2.5",
  "bottom-right": "fixed bottom-7 right-7 z-[120] flex w-[min(92vw,380px)] flex-col gap-2.5",
};

const ICONS = {
  success: { Icon: CheckCircle2, iconStyle: "bg-success/10 text-success ring-success/20", progressColor: "bg-success" },
  warning: { Icon: AlertTriangle, iconStyle: "bg-warning/10 text-warning ring-warning/20", progressColor: "bg-warning" },
  error: { Icon: XCircle, iconStyle: "bg-destructive/10 text-destructive ring-destructive/20", progressColor: "bg-destructive" },
  info: { Icon: Info, iconStyle: "bg-info/10 text-info ring-info/20", progressColor: "bg-info" },
};

function FloatingAlert({ alert, onClose }) {
  const config = ICONS[alert.variant] || ICONS.info;
  const IconComponent = config.Icon;
  const hasBoth = Boolean(alert.title && alert.description);

  return (
    <div
      role="alert"
      data-state={alert.state || "open"}
      className={`pointer-events-auto relative flex items-start gap-3 overflow-hidden rounded-2xl border border-border/80 bg-card/95 p-3.5 text-card-foreground shadow-[0_18px_55px_-18px_rgba(0,0,0,0.55)] backdrop-blur-xl app-floating-alert app-floating-alert--${alert.position || "bottom-right"}`}
      style={{ "--alert-duration": `${Math.max(Number(alert.duration) || 0, 0)}ms` }}
    >
      <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ring-1 ring-inset ${config.iconStyle}`}>
        <IconComponent className="h-[18px] w-[18px]" />
      </span>
      <div className="min-w-0 flex-1 py-0.5 pr-1">
        {alert.title && hasBoth ? (
          <strong className="mb-1 block text-[13px] font-semibold leading-tight text-foreground">
            {alert.title}
          </strong>
        ) : null}
        {alert.description ? (
          <p className={`text-xs leading-[1.55] ${hasBoth ? "text-muted-foreground" : "font-medium text-foreground"}`}>
            {alert.description}
          </p>
        ) : alert.title ? (
          <span className="text-[13px] font-semibold leading-snug text-foreground">
            {alert.title}
          </span>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => onClose(alert.id)}
        className="-mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-accent hover:text-foreground"
        aria-label="Fechar aviso"
      >
        <X className="h-4 w-4" />
      </button>
      {alert.duration > 0 ? (
        <span
          aria-hidden="true"
          className={`app-floating-alert-progress absolute inset-x-0 bottom-0 h-[3px] ${config.progressColor} opacity-80`}
        />
      ) : null}
    </div>
  );
}

export function AlertProvider({ children }) {
  const [alerts, setAlerts] = useState([]);
  const alertsRef = useRef([]);
  const enterMapRef = useRef(new Map());
  const autoDismissMapRef = useRef(new Map());
  const removeMapRef = useRef(new Map());

  useEffect(() => {
    alertsRef.current = alerts;
  }, [alerts]);

  useEffect(() => {
    return () => {
      if (typeof window === "undefined") return;
      [enterMapRef, autoDismissMapRef, removeMapRef].forEach((mapRef) => {
        mapRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
        mapRef.current.clear();
      });
    };
  }, []);

  const clearTimer = useCallback((mapRef, id) => {
    const timeoutId = mapRef.current.get(id);
    if (timeoutId && typeof window !== "undefined") {
      window.clearTimeout(timeoutId);
    }
    mapRef.current.delete(id);
  }, []);

  const removeAlert = useCallback((id) => {
    clearTimer(enterMapRef, id);
    clearTimer(autoDismissMapRef, id);
    clearTimer(removeMapRef, id);
    setAlerts((current) => current.filter((item) => item.id !== id));
  }, [clearTimer]);

  const activateAlert = useCallback((id) => {
    setAlerts((current) =>
      current.map((item) => {
        if (item.id !== id || item.state !== "entering") return item;
        return { ...item, state: "open" };
      })
    );
    clearTimer(enterMapRef, id);
  }, [clearTimer]);

  const dismiss = useCallback((id) => {
    const alert = alertsRef.current.find((item) => item.id === id);
    if (!alert || alert.state === "closing") return;

    clearTimer(enterMapRef, id);
    clearTimer(autoDismissMapRef, id);
    clearTimer(removeMapRef, id);

    setAlerts((current) =>
      current.map((item) => {
        if (item.id !== id) return item;
        return { ...item, state: "closing" };
      })
    );

    if (typeof window === "undefined") return;

    const removeTimeoutId = window.setTimeout(() => removeAlert(id), EXIT_ANIMATION_MS);
    removeMapRef.current.set(id, removeTimeoutId);
  }, [clearTimer, removeAlert]);

  const showAlert = useCallback((options) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const position =
      options?.position === "bottom-center"
        ? "bottom-center"
        : options?.position && options.position !== "top-right"
          ? options.position
          : "bottom-right";

    const next = {
      id,
      title: options?.title || "",
      description: options?.description || "",
      variant: options?.variant || "info",
      position,
      duration: Number.isFinite(options?.duration) ? options.duration : 3200,
      state: "entering",
    };

    setAlerts((current) => [...current, next]);

    if (typeof window !== "undefined") {
      const enterTimeoutId = window.setTimeout(() => activateAlert(id), ENTER_ANIMATION_MS);
      enterMapRef.current.set(id, enterTimeoutId);
    }

    if (next.duration > 0 && typeof window !== "undefined") {
      const timeoutId = window.setTimeout(() => dismiss(id), next.duration);
      autoDismissMapRef.current.set(id, timeoutId);
    }

    return id;
  }, [activateAlert, dismiss]);

  const api = useMemo(() => ({
    showAlert,
    dismiss,
    success: (title, description, options = {}) =>
      showAlert({ title, description, variant: "success", ...options }),
    error: (title, description, options = {}) =>
      showAlert({ title, description, variant: "error", ...options }),
    warning: (title, description, options = {}) =>
      showAlert({ title, description, variant: "warning", ...options }),
    info: (title, description, options = {}) =>
      showAlert({ title, description, variant: "info", ...options }),
  }), [dismiss, showAlert]);

  const groupedAlerts = useMemo(() => {
    return alerts.reduce((accumulator, item) => {
      const key = item.position || "bottom-right";
      if (!accumulator[key]) accumulator[key] = [];
      accumulator[key].push(item);
      return accumulator;
    }, {});
  }, [alerts]);

  return (
    <AlertContext.Provider value={api}>
      {children}
      {Object.entries(groupedAlerts).map(([position, items]) => (
        <div key={position} className={POSITION_CLASSES[position] || POSITION_CLASSES["top-right"]}>
          {items.map((item) => (
            <FloatingAlert key={item.id} alert={item} onClose={dismiss} />
          ))}
        </div>
      ))}
    </AlertContext.Provider>
  );
}

export function useAppAlert() {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error("useAppAlert deve ser usado dentro de AlertProvider");
  }
  return context;
}
